<script setup>
  import { defineRule } from 'vee-validate'
  import { required, email as emailRule } from '@vee-validate/rules'
  import { ref } from 'vue'

  const dialogRef = ref()
  const authStore = useAuthStore()
  const { user } = storeToRefs(authStore)
  const emailSent = ref(false)
  const localePath = useLocalePath()

  defineRule('required', required)
  defineRule('email', emailRule)

  const forgotPasswordSchema = {
    email: 'required|email'
  }

  const { defineField, handleSubmit, errors, resetForm } = useForm({
    validationSchema: forgotPasswordSchema
  })

  const [email] = defineField('email')

  const visible = ref(false)

  function openModal() {
    visible.value = true
    emailSent.value = false
    authStore.error = null
    resetForm()

    // Pre-fill email if user is logged in
    if (user.value?.email) {
      email.value = user.value.email
    }
  }

  function closeModal() {
    visible.value = false
    emailSent.value = false
    resetForm()
  }

  defineExpose({
    openModal,
    closeModal
  })

  const handleForgotPassword = handleSubmit(async (values) => {
    try {
      await authStore.requestPasswordReset(values.email)
      emailSent.value = true
    } catch (error) {
      console.error('Password reset request failed:', error)
    }
  })
</script>

<template>
  <Dialog
    ref="dialogRef"
    v-model:visible="visible"
    modal
    :draggable="false"
    :dismissable-mask="true"
    position="top"
    class="w-sm"
    pt:root="overflow-hidden"
    pt:mask="pt-24 backdrop-blur-xs"
    pt:header="border-b border-slate-200"
    pt:footer="bg-slate-100 !p-4 !justify-center"
  >
    <template #header>
      <h3 class="text-xl font-medium leading-6 text-slate-900">
        {{ $t('pages.auth.forgotPassword.title') }}
      </h3>
    </template>
    <template #closeicon>
      <font-awesome class="fa-fw" :icon="['fas', 'xmark']" />
    </template>

    <div v-if="!emailSent" class="mt-4">
      <p class="text-sm text-slate-600 mb-4">
        {{ $t('pages.auth.forgotPassword.subtitle') }}
      </p>

      <form class="space-y-4" @submit="handleForgotPassword">
        <div>
          <label for="email" class="block text-sm font-medium text-slate-700">
            {{ $t('pages.auth.forgotPassword.form.email') }}
          </label>
          <div class="mt-1">
            <InputText
              id="email"
              v-model="email"
              :invalid="errors.email"
              fluid
              autofocus
              type="email"
              autocomplete="email"
            />
            <Message severity="error" size="small" variant="simple">{{ errors.email }}</Message>
          </div>
        </div>

        <Message v-if="authStore.error" class="mb-2" severity="error" size="small" variant="error">
          {{ $t('pages.auth.forgotPassword.error.generic') }}
          <NuxtLink :to="localePath('/contact')" class="underline font-medium">
            {{ $t('pages.auth.forgotPassword.error.contactUs') }}
          </NuxtLink>
        </Message>

        <div>
          <Button
            type="submit"
            fluid
            :loading="authStore.loadingResetPassword"
            :disabled="authStore.loadingResetPassword"
          >
            {{ $t('pages.auth.forgotPassword.form.submit') }}
          </Button>
        </div>
      </form>
    </div>

    <div v-else class="mt-4">
      <div class="text-center">
        <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-emerald-100 mb-4">
          <font-awesome class="text-emerald-600" :icon="['fas', 'check']" />
        </div>
        <h3 class="text-lg font-medium text-slate-900 mb-2">
          {{ $t('pages.auth.forgotPassword.success.title') }}
        </h3>
        <p class="text-sm text-slate-600">
          {{ $t('pages.auth.forgotPassword.success.subtitle') }}
        </p>
      </div>
    </div>
  </Dialog>
</template>
